// A league's scoring as a table anyone can read, and the edits its editor
// makes (PLAN-2026-10-18-scoring-anyone-can-read §2.2 to §3).
//
// ONE COLUMN, A SECTION PER POSITION. Each row is a plain label and the
// points that position actually scores by: its own rule if it has one, else
// everyone's — the same precedence the engine uses (`@fsp/scoring`'s
// `resolve`), so the table can never say a number the engine does not score.
//
// SPORT-NEUTRAL. The words, the units, the kinds of play and which rows a
// newcomer looks for come from the sport (`StatDef.scoring`,
// `SportDefinition.scoringLayout`); a sport without them still gets a table,
// by stat category, with the catalog's names.
//
// THE USUAL NUMBERS a league is compared against are the preset its setup
// answers would give that is closest to what it has (`usualScoringFor`), so a
// league needs nothing stored to say what it started from, and "3 numbers
// changed" is a difference of two scoring configs.

import { z } from 'zod';
import type { PositionId, ScoringConfigLike, SportDefinition, StatDef } from '@fsp/sports-core';
import type { BonusRule, ScoringConfig, TierRule, UnitRule } from './schemas';
import type { Issue } from './validate';

// ── The wire shape ─────────────────────────────────────────────────────────

export const ScoringRow = z.object({
  /** Stable within a table: `unit:rec`, `bonus:pass_yd:300:game`, `band:dst_pts_allowed:0`, `range:fg:fg_0_19..fg_30_39`. */
  id: z.string(),
  kind: z.enum(['unit', 'bonus', 'band']),
  /** The stat, or a merged range's stats in order. */
  stats: z.array(z.string()).min(1),
  label: z.string(),
  /** Points per `unit` of the stat, a bonus's points, or a band's. */
  points: z.number(),
  /** Counted per this many: 25 for "every 25 passing yards". 1 for a bonus or a band. */
  unit: z.number().positive(),
  /** The units the editor offers for this row; one entry when there is no choice. */
  units: z.array(z.number().positive()),
  /** A bonus's threshold. */
  threshold: z.number().nullable(),
  /** A bonus's or band's: per game or on the week's total. */
  scope: z.enum(['game', 'period']).nullable(),
  /** A bonus on top of a lower one on the same stat: "+2 more". */
  stacked: z.boolean(),
  /** This position has a number of its own, not everyone's. */
  own: z.boolean(),
  /** The usual number for this row, when it is not this one. */
  usual: z.number().nullable(),
  /** Offered by the editor at 0: "More you can score". */
  offered: z.boolean(),
  /** What a search matches: the label, the stat's names and the words experienced players use. */
  words: z.array(z.string()),
});
export type ScoringRow = z.infer<typeof ScoringRow>;

export const ScoringGroup = z.object({
  id: z.string(),
  title: z.string(),
  rows: z.array(ScoringRow),
});
export type ScoringGroup = z.infer<typeof ScoringGroup>;

export const ScoringSection = z.object({
  position: z.string(),
  /** 'Quarterbacks'. */
  title: z.string(),
  /** The kinds of play this position earns its points in, in that order. */
  groups: z.array(ScoringGroup),
  /** Everything else it can score, behind one line. */
  more: z.array(ScoringGroup),
});
export type ScoringSection = z.infer<typeof ScoringSection>;

export const ScoringTable = z.object({
  /** "The usual numbers, with points for catches and a team’s defense", or "Custom: 3 numbers changed from the usual". */
  heading: z.string(),
  /** Short, for a subtitle: "Points for catches, a team’s defense" or "Custom scoring". */
  headline: z.string(),
  /** Rules that differ from the usual numbers. */
  changed: z.number().int().min(0),
  /** The preset the usual numbers are, or null when the sport has none. */
  usualPresetId: z.string().nullable(),
  decimals: z.number().int().min(0).max(2),
  sections: z.array(ScoringSection),
});
export type ScoringTable = z.infer<typeof ScoringTable>;

/** One of the create form's scoring questions, as `GET /api/sports` serves it. */
export const ScoringQuestion = z.object({
  id: z.string(),
  label: z.string(),
  style: z.enum(['switch', 'choice']),
  default: z.string(),
  options: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
      sentence: z.string(),
      phrase: z.string(),
      /** False for an option the sport cannot play yet: shown greyed, refused if sent. */
      available: z.boolean(),
    }),
  ),
});
export type ScoringQuestion = z.infer<typeof ScoringQuestion>;

/** The sport's scoring questions without the function behind them. */
export function scoringQuestionsOf(sport: SportDefinition): ScoringQuestion[] {
  return (sport.scoringSetup?.questions ?? []).map((q) => ({
    id: q.id,
    label: q.label,
    style: q.style,
    default: q.default,
    options: q.options.map((o) => ({ ...o })),
  }));
}

// ── The limits ─────────────────────────────────────────────────────────────

/**
 * What a league may choose (§2.3): a first cut, widened when a league asks.
 * A unit rule's points are counted per the unit the table shows it in.
 */
export const SCORING_LIMITS = {
  points: 10,
  bonusPoints: 10,
  bandPoints: 20,
  threshold: { min: 1, max: 999 },
} as const;

// ── Helpers ────────────────────────────────────────────────────────────────

type Config = ScoringConfig | ScoringConfigLike;
type Unit = Config['units'][number];
type Bonus = Config['bonuses'][number];
type Tier = Config['tiers'][number];

const tidy = (x: number): number => Math.round(x * 1e6) / 1e6;

function num(x: number): string {
  return Number.isInteger(x) ? String(x) : String(tidy(x));
}

function posKey(positions: readonly string[] | null): string {
  return positions === null ? '*' : [...positions].sort().join('+');
}

function matches(positions: readonly string[] | null, position: PositionId): boolean {
  return positions === null || positions.includes(position);
}

/** The rule a position scores a stat by: its own first, else everyone's (the engine's precedence). */
function unitFor(
  config: Config,
  stat: string,
  position: PositionId,
): { rule: Unit; own: boolean } | null {
  const hits = config.units.filter((u) => u.stat === stat && matches(u.positions, position));
  const own = hits.find((u) => u.positions !== null);
  if (own) return { rule: own, own: true };
  return hits[0] ? { rule: hits[0], own: false } : null;
}

function bonusesFor(config: Config, position: PositionId): { rule: Bonus; own: boolean }[] {
  const groups = new Map<string, Bonus[]>();
  for (const b of config.bonuses) {
    if (!matches(b.positions, position)) continue;
    const k = `${b.stat}@${b.threshold}@${b.scope}`;
    groups.set(k, [...(groups.get(k) ?? []), b]);
  }
  return [...groups.values()].map((g) => {
    const own = g.find((b) => b.positions !== null);
    return own ? { rule: own, own: true } : { rule: g[0]!, own: false };
  });
}

/** The band sets a position scores a stat by, one per scope, its own first (the engine's `stat@scope`). */
function tiersFor(
  config: Config,
  stat: string,
  position: PositionId,
): { rule: Tier; own: boolean }[] {
  const groups = new Map<string, Tier[]>();
  for (const t of config.tiers) {
    if (t.stat !== stat || !matches(t.positions, position)) continue;
    groups.set(t.scope, [...(groups.get(t.scope) ?? []), t]);
  }
  return [...groups.values()].map((g) => {
    const own = g.find((t) => t.positions !== null);
    return own ? { rule: own, own: true } : { rule: g[0]!, own: false };
  });
}

/** A unit rule as the table shows it: points per a unit. */
function shown(
  rule: Pick<Unit, 'points' | 'per'>,
  stat: StatDef | undefined,
): { points: number; unit: number } {
  if (rule.per !== 1) return { points: tidy(rule.points), unit: rule.per };
  const unit = stat?.scoring?.unit ?? 1;
  return { points: tidy(rule.points * unit), unit };
}

function rate(rule: Pick<Unit, 'points' | 'per'>): number {
  return tidy(rule.points / rule.per);
}

function unitLabel(stat: StatDef | undefined, key: string, unit: number): string {
  const w = stat?.scoring;
  if (!w) return stat?.name ?? key;
  return unit === 1 ? w.label : `Every ${num(unit)} ${w.plural}`;
}

function bandLabel(stat: StatDef | undefined, band: { min: number; max: number | null }): string {
  const b = stat?.scoring?.bands;
  const noun = b?.noun ?? stat?.name.toLowerCase() ?? '';
  if (band.min === 0 && band.max === 0 && b?.zero) return b.zero;
  if (band.max === null) return `${num(band.min)} or more ${noun}`;
  if (band.min === band.max) return `${num(band.min)} ${noun}`;
  return `${num(band.min)}–${num(band.max)} ${noun}`;
}

function rangeText(min: number, max: number | null): string {
  if (max === null) return min === 0 ? 'any distance' : `${num(min)} or more`;
  if (min === 0) return `under ${num(max + 1)}`;
  return `${num(min)}–${num(max)}`;
}

function searchWords(stat: StatDef | undefined, label: string, group: string): string[] {
  return [
    label,
    group,
    ...(stat ? [stat.name, stat.shortName] : []),
    ...(stat?.scoring?.aliases ?? []),
  ].map((x) => x.toLowerCase());
}

interface Layout {
  groups: { id: string; title: string; stats: readonly string[] }[];
  sections: Map<string, { title: string; main: readonly string[]; always: readonly string[] }>;
  series: Map<string, string>;
}

/** The sport's layout, or one by stat category for a sport that has none. */
function layoutOf(sport: SportDefinition): Layout {
  const l = sport.scoringLayout;
  if (l) {
    return {
      groups: l.groups.map((g) => ({ ...g })),
      sections: new Map(l.sections.map((s) => [s.position, s])),
      series: new Map((l.series ?? []).map((s) => [s.id, s.label])),
    };
  }
  const groups = new Map<string, string[]>();
  for (const st of sport.stats) {
    if (!st.scorable || st.category === 'research') continue;
    groups.set(st.category, [...(groups.get(st.category) ?? []), st.key]);
  }
  return {
    groups: [...groups].map(([id, stats]) => ({
      id,
      title: id.charAt(0).toUpperCase() + id.slice(1),
      stats,
    })),
    sections: new Map(),
    series: new Map(),
  };
}

// ── Setup: the create form's questions ────────────────────────────────────

/** Every full set of answers the sport's questions allow. */
export function setupCombos(sport: SportDefinition): Record<string, string>[] {
  let combos: Record<string, string>[] = [{}];
  for (const q of sport.scoringSetup?.questions ?? [])
    combos = combos.flatMap((c) => q.options.map((o) => ({ ...c, [q.id]: o.value })));
  return sport.scoringSetup ? combos : [];
}

/** The answers with every unanswered question at its default; null for a sport with no questions. */
export function setupAnswers(
  sport: SportDefinition,
  given: Readonly<Record<string, string>> = {},
): Record<string, string> | null {
  const setup = sport.scoringSetup;
  if (!setup) return null;
  return Object.fromEntries(setup.questions.map((q) => [q.id, given[q.id] ?? q.default]));
}

/** Why a set of answers cannot make a league, or null. */
export function setupRefusal(
  sport: SportDefinition,
  given: Readonly<Record<string, string>>,
): string | null {
  const setup = sport.scoringSetup;
  if (!setup) return `${sport.name} asks no scoring questions.`;
  for (const [id, value] of Object.entries(given)) {
    const q = setup.questions.find((x) => x.id === id);
    if (!q) return `${sport.name} asks no question called ${id}.`;
    const o = q.options.find((x) => x.value === value);
    if (!o) return `${q.label}: ${value} is not one of the answers.`;
    if (!o.available) return `${q.label}: ${o.label.toLowerCase()} cannot be played yet.`;
  }
  return null;
}

/** The scoring and roster presets the answers give. */
export function setupPresets(
  sport: SportDefinition,
  given: Readonly<Record<string, string>> = {},
): { scoring: string; roster: string } | null {
  const answers = setupAnswers(sport, given);
  return answers === null ? null : sport.scoringSetup!.presets(answers);
}

// ── Comparing two scoring configs ──────────────────────────────────────────

/** The rules that differ between two scoring configs, one key each. A rule worth 0 counts as no rule. */
export function scoringChanges(a: Config, b: Config): string[] {
  const units = (c: Config) => {
    const m = new Map<string, string>();
    for (const u of c.units) {
      const k = `unit|${u.stat}|${posKey(u.positions)}`;
      if (m.has(k)) continue;
      // Rounding is compared only where the engine applies it: a count of more than one.
      if (u.points !== 0 || u.positions !== null)
        m.set(k, `${rate(u)}|${u.per === 1 ? 'none' : u.rounding}`);
    }
    return m;
  };
  const bonuses = (c: Config) => {
    const m = new Map<string, string>();
    for (const x of c.bonuses) {
      const k = `bonus|${x.stat}|${x.threshold}|${x.scope}|${posKey(x.positions)}`;
      if (!m.has(k) && (x.points !== 0 || x.positions !== null)) m.set(k, String(x.points));
    }
    return m;
  };
  const bands = (c: Config) => {
    const m = new Map<string, string>();
    for (const t of c.tiers)
      for (const band of t.bands) {
        const k = `band|${t.stat}|${t.scope}|${posKey(t.positions)}|${band.min}-${band.max}`;
        if (!m.has(k) && band.points !== 0) m.set(k, String(band.points));
      }
    return m;
  };
  const out: string[] = [];
  for (const [fa, fb] of [
    [units(a), units(b)],
    [bonuses(a), bonuses(b)],
    [bands(a), bands(b)],
  ] as const) {
    for (const k of new Set([...fa.keys(), ...fb.keys()])) if (fa.get(k) !== fb.get(k)) out.push(k);
  }
  if (a.decimals !== b.decimals) out.push('decimals');
  return out;
}

/** The preset a league's setup answers could give that is closest to its scoring. */
export function usualScoringFor(
  scoring: Config,
  sport: SportDefinition,
): { presetId: string; config: ScoringConfigLike } | null {
  const ids = sport.scoringSetup
    ? [...new Set(setupCombos(sport).map((c) => sport.scoringSetup!.presets(c).scoring))]
    : sport.scoringPresets.map((p) => p.id);
  let best: { presetId: string; config: ScoringConfigLike; d: number } | null = null;
  for (const id of ids) {
    const preset = sport.scoringPresets.find((p) => p.id === id);
    if (!preset) continue;
    const d = scoringChanges(scoring, preset.config).length;
    if (best === null || d < best.d) best = { presetId: id, config: preset.config, d };
  }
  return best && { presetId: best.presetId, config: best.config };
}

/** The positions a roster can start, in the sport's order. */
export function startingPositions(
  roster: { slots: readonly { slot: string; count: number }[] },
  sport: SportDefinition,
): PositionId[] {
  const eligible = new Set<string>();
  for (const { slot, count } of roster.slots) {
    if (count <= 0) continue;
    const def = sport.slots.find((s) => s.id === slot);
    if (def?.kind !== 'starter') continue;
    // A starting slot that names nobody takes anybody (`SlotDef.eligible`).
    const takes = def.eligible ?? [];
    for (const p of takes.length > 0 ? takes : sport.positions.map((x) => x.id)) eligible.add(p);
  }
  return [...sport.positions]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .filter((p) => eligible.has(p.id))
    .map((p) => p.id);
}

// ── The table ──────────────────────────────────────────────────────────────

export interface ScoringTableOptions {
  /** The positions the league starts, in order (`startingPositions`). */
  positions: readonly PositionId[];
  /** The editor's table: every row it can change, zeros included, ranges unmerged. */
  editing?: boolean;
  /** What to compare against; absent is `usualScoringFor`, null is nothing. */
  usual?: { presetId: string | null; config: Config } | null;
}

export function scoringTable(
  scoring: Config,
  sport: SportDefinition,
  options: ScoringTableOptions,
): ScoringTable {
  const editing = options.editing === true;
  const usual = options.usual === undefined ? usualScoringFor(scoring, sport) : options.usual;
  const stats = new Map(sport.stats.map((s) => [s.key, s]));
  const layout = layoutOf(sport);
  const applies = (key: string, position: PositionId): boolean => {
    const st = stats.get(key);
    return st !== undefined && (st.positions === undefined || st.positions.includes(position));
  };

  const sectionOf = (position: PositionId): ScoringSection => {
    const def = sport.positions.find((p) => p.id === position);
    const shape = layout.sections.get(position) ?? {
      title: def?.name ?? position,
      main: layout.groups.map((g) => g.id),
      always: [] as readonly string[],
    };

    const rowsOf = (group: { id: string; title: string; stats: readonly string[] }) => {
      const rows: ScoringRow[] = [];
      for (const key of group.stats) {
        if (!applies(key, position)) continue;
        const st = stats.get(key)!;
        if (st.kind === 'tiered') {
          rows.push(...bandRows(key, st, group.title));
          continue;
        }
        const eff = unitFor(scoring, key, position);
        const got = eff ? shown(eff.rule, st) : { points: 0, unit: st.scoring?.unit ?? 1 };
        const always = shape.always.includes(key);
        const offerable = editing && st.scorable && st.scoring !== undefined;
        if (got.points === 0 && !always && !offerable && !(eff && eff.own)) continue;
        const u = usual ? unitFor(usual.config, key, position) : null;
        const usualRate = u ? rate(u.rule) : 0;
        // Rounding means something only for a count of more than one: the
        // engine ignores it at one (`scorePlayer`).
        const counted = eff !== null && eff.rule.per !== 1;
        const label =
          unitLabel(st, key, got.unit) +
          (counted && eff.rule.rounding === 'floor'
            ? ', whole ones only'
            : counted && eff.rule.rounding === 'round'
              ? ', rounded'
              : '');
        rows.push({
          id: `unit:${key}`,
          kind: 'unit',
          stats: [key],
          label,
          points: got.points,
          unit: got.unit,
          units: [...(st.scoring?.units ?? [1])],
          threshold: null,
          scope: null,
          stacked: false,
          own: eff?.own ?? false,
          usual:
            usual && usualRate !== tidy(got.points / got.unit) ? tidy(usualRate * got.unit) : null,
          offered: editing && got.points === 0 && !always && eff === null,
          words: searchWords(st, label, group.title),
        });
      }
      // Bonuses sit with the play they reward, after its rows, lowest first.
      const bonuses = bonusesFor(scoring, position)
        .filter(({ rule }) => group.stats.includes(rule.stat) && applies(rule.stat, position))
        .sort(
          (a, b) =>
            group.stats.indexOf(a.rule.stat) - group.stats.indexOf(b.rule.stat) ||
            a.rule.threshold - b.rule.threshold,
        );
      const usualBonuses = usual ? bonusesFor(usual.config, position) : [];
      let last = '';
      for (const { rule, own } of bonuses) {
        if (rule.points === 0 && !editing) continue;
        const st = stats.get(rule.stat);
        const key = `${rule.stat}@${rule.scope}`;
        const stacked = key === last;
        last = key;
        const label = `${num(rule.threshold)} ${st?.scoring?.plural ?? st?.name.toLowerCase() ?? rule.stat} in a ${rule.scope === 'game' ? 'game' : 'week'}`;
        const was = usualBonuses.find(
          (x) =>
            x.rule.stat === rule.stat &&
            x.rule.threshold === rule.threshold &&
            x.rule.scope === rule.scope,
        );
        const usualPoints = was ? was.rule.points : 0;
        rows.push({
          id: `bonus:${rule.stat}:${rule.threshold}:${rule.scope}`,
          kind: 'bonus',
          stats: [rule.stat],
          label,
          points: rule.points,
          unit: 1,
          units: [1],
          threshold: rule.threshold,
          scope: rule.scope,
          stacked,
          own,
          usual: usual && usualPoints !== rule.points ? usualPoints : null,
          offered: false,
          words: searchWords(st, label, group.title).concat('bonus'),
        });
      }
      return editing ? rows : mergeRanges(rows);
    };

    const bandRows = (key: string, st: StatDef, groupTitle: string): ScoringRow[] => {
      const always = shape.always.includes(key);
      const defaults = st.scoring?.bands?.defaults;
      // A STAT WITH NO BANDS YET: the editor offers the sport's at 0, and one
      // a newcomer looks for (points allowed) shows them at 0 rather than
      // vanishing, so a set of bands all worth nothing can be brought back.
      const sets: { rule: Tier; own: boolean; fresh: boolean }[] = tiersFor(
        scoring,
        key,
        position,
      ).map((t) => ({ ...t, fresh: false }));
      if (sets.length === 0 && (editing || always) && defaults)
        sets.push({
          rule: {
            stat: key,
            bands: defaults.map((b) => ({ min: b.min, max: b.max, points: 0 })),
            scope: 'game',
            positions: null,
          },
          own: false,
          fresh: true,
        });
      const usualSets = usual ? tiersFor(usual.config, key, position) : [];
      return sets.flatMap(({ rule, own, fresh }) => {
        const was = usualSets.find((u) => u.rule.scope === rule.scope);
        return rule.bands.map((band, index) => {
          const label = bandLabel(st, band) + (rule.scope === 'period' ? ' in a week' : '');
          const prior = was?.rule.bands.find((b) => b.min === band.min && b.max === band.max);
          const usualPoints = prior ? prior.points : 0;
          return {
            id: `band:${key}:${rule.scope}:${index}`,
            kind: 'band' as const,
            stats: [key],
            label,
            points: band.points,
            unit: 1,
            units: [1],
            threshold: null,
            scope: rule.scope,
            stacked: false,
            own,
            usual: usual && usualPoints !== band.points ? usualPoints : null,
            offered: fresh && !always,
            words: searchWords(st, label, groupTitle).concat(st.scoring?.bands?.noun ?? ''),
          };
        });
      });
    };

    /** Equal neighbors in a series read as one row: "Field goal, under 40 yards". */
    const mergeRanges = (rows: ScoringRow[]): ScoringRow[] => {
      const out: ScoringRow[] = [];
      for (const row of rows) {
        const range = row.kind === 'unit' ? stats.get(row.stats[0]!)?.scoring?.range : undefined;
        const prev = out[out.length - 1];
        const prevRange =
          prev && prev.kind === 'unit'
            ? stats.get(prev.stats[prev.stats.length - 1]!)?.scoring?.range
            : undefined;
        if (
          range &&
          prev &&
          prevRange &&
          prevRange.series === range.series &&
          prev.points === row.points &&
          prev.unit === 1 &&
          row.unit === 1 &&
          prev.own === row.own &&
          prev.usual === null &&
          row.usual === null
        ) {
          const first = stats.get(prev.stats[0]!)!.scoring!.range!;
          const stats2 = [...prev.stats, ...row.stats];
          const label = seriesLabel(range.series, first.min, range.max);
          out[out.length - 1] = {
            ...prev,
            id: `range:${range.series}:${stats2[0]}..${stats2[stats2.length - 1]}`,
            stats: stats2,
            label,
            words: [...new Set([...prev.words, ...row.words, label.toLowerCase()])],
          };
          continue;
        }
        if (range) {
          const label = seriesLabel(range.series, range.min, range.max);
          out.push({ ...row, label, words: [...row.words, label.toLowerCase()] });
        } else out.push(row);
      }
      return out;
    };

    const seriesLabel = (series: string, min: number, max: number | null): string =>
      (layout.series.get(series) ?? '{range}').replace('{range}', rangeText(min, max));

    const groups: ScoringGroup[] = [];
    const more: ScoringGroup[] = [];
    for (const id of shape.main) {
      const g = layout.groups.find((x) => x.id === id);
      if (!g) continue;
      const rows = rowsOf(g);
      if (rows.length) groups.push({ id: g.id, title: g.title, rows });
    }
    for (const g of layout.groups) {
      if (shape.main.includes(g.id)) continue;
      const rows = rowsOf(g);
      if (rows.length) more.push({ id: g.id, title: g.title, rows });
    }
    return { position, title: shape.title, groups, more };
  };

  // THE RULES THAT DIFFER are what a row can show tinted; whole points or
  // decimals has no row, so it is said in the heading instead of counted.
  const differences = usual ? scoringChanges(scoring, usual.config) : [];
  const changed = differences.filter((k) => k !== 'decimals').length;
  const decimalsWords = differences.includes('decimals')
    ? scoring.decimals === 0
      ? ', in whole points'
      : `, kept to ${scoring.decimals === 1 ? 'one decimal' : 'two decimals'}`
    : '';
  const words = usual ? setupWords(sport, usual.presetId, options.positions) : null;
  const usualName = usual
    ? (sport.scoringPresets.find((p) => p.id === usual.presetId)?.name ?? null)
    : null;
  const heading =
    changed > 0
      ? `Custom: ${changed} ${changed === 1 ? 'number' : 'numbers'} changed from the usual${decimalsWords}`
      : words
        ? `The usual numbers, with ${words}${decimalsWords}`
        : `${usualName ?? 'Custom scoring'}${decimalsWords}`;
  const headline =
    changed > 0
      ? 'Custom scoring'
      : words
        ? words.charAt(0).toUpperCase() + words.slice(1).replace(' and ', ', ')
        : (usualName ?? 'Custom scoring');

  return {
    heading,
    headline,
    changed,
    usualPresetId: usual?.presetId ?? null,
    decimals: scoring.decimals,
    sections: options.positions.map(sectionOf),
  };
}

/**
 * The setup's own words for a preset: "points for catches and a team’s
 * defense". The answers chosen are the ones that give this preset and a
 * roster starting these positions; when no answers' roster is this league's
 * (a best-ball roster with no defense), there are no setup words for it, and
 * the preset's own name is used rather than words that describe another
 * roster.
 */
function setupWords(
  sport: SportDefinition,
  presetId: string | null,
  positions: readonly PositionId[],
): string | null {
  const setup = sport.scoringSetup;
  if (!setup || presetId === null) return null;
  const combos = setupCombos(sport).filter((c) => setup.presets(c).scoring === presetId);
  const fits = combos.find((c) => {
    const roster = sport.rosterPresets.find((r) => r.id === setup.presets(c).roster);
    if (!roster) return false;
    const starts = startingPositions(roster, sport);
    return starts.length === positions.length && starts.every((p) => positions.includes(p));
  });
  const answers = fits;
  if (!answers) return null;
  const phrases = setup.questions.map(
    (q) => q.options.find((o) => o.value === answers[q.id])?.phrase ?? '',
  );
  const said = phrases.filter(Boolean);
  if (said.length <= 1) return said[0] ?? null;
  return `${said.slice(0, -1).join(', ')} and ${said[said.length - 1]}`;
}

// ── Edits ──────────────────────────────────────────────────────────────────

export type ScoringEdit =
  /** A stat's points per `unit`, for every position (`position: null`) or one. */
  | { kind: 'unit'; stat: string; position: PositionId | null; points: number; unit?: number }
  /** Give a position everyone's number back. */
  | { kind: 'own-clear'; stat: string; position: PositionId }
  /** A bonus's points and threshold. 0 points removes it. */
  | {
      kind: 'bonus';
      stat: string;
      threshold: number;
      scope: 'game' | 'period';
      position: PositionId | null;
      points: number;
      newThreshold?: number;
    }
  | { kind: 'bonus-add'; stat: string; threshold: number; points: number }
  | { kind: 'bonus-remove'; stat: string; threshold: number; scope: 'game' | 'period' }
  /**
   * One band's points, in the band set the position scores by at that scope
   * (everyone's when no position is named); a stat with no bands yet starts
   * from the sport's.
   */
  | {
      kind: 'band';
      stat: string;
      index: number;
      points: number;
      scope?: 'game' | 'period';
      position?: PositionId | null;
    }
  | { kind: 'decimals'; decimals: number };

/**
 * The scoring after one edit. Pure; the result is custom (`presetId: null`)
 * unless the edit changed nothing, which returns the input as it was.
 */
export function editScoring(
  scoring: ScoringConfig,
  edit: ScoringEdit,
  sport: SportDefinition,
): ScoringConfig {
  const next = applyEdit(scoring, edit, sport);
  return scoringChanges(scoring, next).length === 0 ? scoring : { ...next, presetId: null };
}

function applyEdit(s: ScoringConfig, edit: ScoringEdit, sport: SportDefinition): ScoringConfig {
  const units = s.units.map((u) => ({ ...u, positions: u.positions ? [...u.positions] : null }));
  const bonuses = s.bonuses.map((b) => ({
    ...b,
    positions: b.positions ? [...b.positions] : null,
  }));
  const tiers = s.tiers.map((t) => ({
    ...t,
    bands: t.bands.map((b) => ({ ...b })),
    positions: t.positions ? [...t.positions] : null,
  }));
  const stat = sport.stats.find((x) => 'stat' in edit && x.key === edit.stat);

  switch (edit.kind) {
    case 'decimals':
      return { ...s, units, bonuses, tiers, decimals: edit.decimals };

    case 'unit': {
      const unit = edit.unit ?? shownUnit(units, edit.stat, edit.position, stat);
      const want = { points: tidy(edit.points), per: unit };
      const wantRate = rate(want);
      const store = (u: UnitRule): void => {
        // Keep a rule's own way of counting when the number is the same in it:
        // 0.04 a yard stays 0.04 a yard rather than becoming 1 every 25.
        if (rate(u) === wantRate) return;
        u.points = want.points;
        u.per = want.per;
        // The table has no rounding to offer, so a number written from it
        // scores as the table says: fractions count.
        u.rounding = 'none';
      };
      const general = units.find((u) => u.stat === edit.stat && u.positions === null);
      if (edit.position === null) {
        if (wantRate === 0) {
          if (general) units.splice(units.indexOf(general), 1);
        } else if (general) store(general);
        else units.push({ stat: edit.stat, ...want, rounding: 'none', positions: null });
        // A position whose own number is now everyone's has no number of its own.
        for (const u of units.filter((x) => x.stat === edit.stat && x.positions !== null))
          if (rate(u) === wantRate) units.splice(units.indexOf(u), 1);
        return { ...s, units, bonuses, tiers };
      }
      const p = edit.position;
      const everyone = general ? rate(general) : 0;
      // Out of any rule it shares with other positions.
      for (const u of units.filter(
        (x) => x.stat === edit.stat && x.positions !== null && x.positions.includes(p),
      )) {
        if (u.positions!.length === 1) {
          if (wantRate !== everyone) {
            store(u);
            return { ...s, units, bonuses, tiers };
          }
          units.splice(units.indexOf(u), 1);
        } else u.positions = u.positions!.filter((x) => x !== p);
      }
      if (wantRate !== everyone)
        units.push({ stat: edit.stat, ...want, rounding: 'none', positions: [p] });
      return { ...s, units, bonuses, tiers };
    }

    case 'own-clear': {
      const kept = units.flatMap((u) => {
        if (u.stat !== edit.stat || u.positions === null || !u.positions.includes(edit.position))
          return [u];
        const rest = u.positions.filter((x) => x !== edit.position);
        return rest.length ? [{ ...u, positions: rest }] : [];
      });
      return { ...s, units: kept, bonuses, tiers };
    }

    case 'bonus': {
      // AS A UNIT EDIT DOES: everyone's bonus for every position, or one
      // position's own, which leaves everyone else's alone — a 0 of its own
      // where everyone's would otherwise reach it, and a shared rule narrowed
      // rather than deleted (the review of chunks 1 and 2).
      const at = (b: BonusRule, threshold: number) =>
        b.stat === edit.stat && b.threshold === threshold && b.scope === edit.scope;
      const to = edit.newThreshold ?? edit.threshold;
      const general = (threshold: number) =>
        bonuses.find((b) => at(b, threshold) && b.positions === null);
      if (edit.position === null) {
        const g = general(edit.threshold);
        if (edit.points === 0) {
          if (g) bonuses.splice(bonuses.indexOf(g), 1);
        } else if (g) {
          g.points = edit.points;
          g.threshold = to;
        } else
          bonuses.push({
            stat: edit.stat,
            threshold: to,
            points: edit.points,
            scope: edit.scope,
            positions: null,
          });
        return { ...s, units, bonuses, tiers };
      }
      const p = edit.position;
      const own = (threshold: number, points: number) =>
        bonuses.push({ stat: edit.stat, threshold, points, scope: edit.scope, positions: [p] });
      // Out of any rule it shares, at the threshold it had.
      for (const b of bonuses.filter((x) => at(x, edit.threshold) && x.positions?.includes(p))) {
        if (b.positions!.length === 1) bonuses.splice(bonuses.indexOf(b), 1);
        else b.positions = b.positions!.filter((x) => x !== p);
      }
      if (to !== edit.threshold) {
        // Moved for this position alone: it no longer scores at the old one.
        if (general(edit.threshold)) own(edit.threshold, 0);
        const there = general(to);
        if (!there || there.points !== edit.points) own(to, edit.points);
      } else {
        const g = general(edit.threshold);
        if (!g || g.points !== edit.points) own(edit.threshold, edit.points);
      }
      // A position's own 0 where nobody else's bonus would reach it is no rule.
      const kept = bonuses.filter(
        (b) => !(b.positions !== null && b.points === 0 && !general(b.threshold)),
      );
      return { ...s, units, bonuses: kept, tiers };
    }

    case 'bonus-add': {
      const same = bonuses.find(
        (b) =>
          b.stat === edit.stat &&
          b.threshold === edit.threshold &&
          b.scope === 'game' &&
          b.positions === null,
      );
      if (same) same.points = edit.points;
      else
        bonuses.push({
          stat: edit.stat,
          threshold: edit.threshold,
          points: edit.points,
          scope: 'game',
          positions: null,
        });
      return { ...s, units, bonuses, tiers };
    }

    case 'bonus-remove': {
      const kept = bonuses.filter(
        (b) => !(b.stat === edit.stat && b.threshold === edit.threshold && b.scope === edit.scope),
      );
      return { ...s, units, bonuses: kept, tiers };
    }

    case 'band': {
      const scope = edit.scope ?? 'game';
      const position = edit.position ?? null;
      const hits = tiers.filter(
        (t) =>
          t.stat === edit.stat &&
          t.scope === scope &&
          (position === null ? t.positions === null : matches(t.positions, position)),
      );
      let tier: TierRule | undefined = hits.find((t) => t.positions !== null) ?? hits[0];
      if (!tier) {
        const defaults = stat?.scoring?.bands?.defaults;
        if (!defaults) return { ...s, units, bonuses, tiers };
        tier = {
          stat: edit.stat,
          bands: defaults.map((b) => ({ min: b.min, max: b.max, points: 0 })),
          scope,
          positions: null,
        };
        tiers.push(tier);
      }
      const band = tier.bands[edit.index];
      if (band) band.points = edit.points;
      // Bands that are all worth nothing are no rule.
      const kept = tiers.filter((t) => t.bands.some((b) => b.points !== 0));
      return { ...s, units, bonuses, tiers: kept };
    }
  }
}

/** The unit a stat's row shows now, for an edit that does not name one. */
function shownUnit(
  units: readonly UnitRule[],
  key: string,
  position: PositionId | null,
  stat: StatDef | undefined,
): number {
  const rule =
    (position === null
      ? undefined
      : units.find((u) => u.stat === key && u.positions?.includes(position))) ??
    units.find((u) => u.stat === key && u.positions === null);
  return rule ? shown(rule, stat).unit : (stat?.scoring?.unit ?? 1);
}

// ── Limits ─────────────────────────────────────────────────────────────────

/**
 * Every rule outside what a league may choose (`SCORING_LIMITS`), named by
 * its row. A rule that is exactly as it was in `before` is not judged again,
 * so a league whose scoring predates the limits can still save anything else.
 */
export function scoringLimitIssues(
  scoring: ScoringConfig,
  sport: SportDefinition,
  before?: ScoringConfig,
): Issue[] {
  const issues: Issue[] = [];
  const err = (path: string, message: string) => issues.push({ path, severity: 'error', message });
  const stats = new Map(sport.stats.map((x) => [x.key, x]));
  const same = (a: unknown, list: readonly unknown[] | undefined) =>
    (list ?? []).some((b) => JSON.stringify(b) === JSON.stringify(a));
  const outside = (value: number, most: number): string | null =>
    value > most
      ? `${num(value)} is more than ${most}, the most a rule can be worth`
      : value < -most
        ? `${num(value)} is less than −${most}, the least a rule can be worth`
        : null;

  scoring.units.forEach((u, i) => {
    if (same(u, before?.units)) return;
    const st = stats.get(u.stat);
    const got = shown(u, st);
    const label = unitLabel(st, u.stat, got.unit);
    const bad = outside(got.points, SCORING_LIMITS.points);
    if (bad) err(`scoring.units.${i}`, `${label}: ${bad}.`);
    const offered = st?.scoring?.units;
    if (u.per !== 1 && offered && !offered.includes(u.per))
      err(`scoring.units.${i}`, `${label}: every ${num(u.per)} is not one of the choices.`);
  });
  scoring.bonuses.forEach((b, i) => {
    if (same(b, before?.bonuses)) return;
    const st = stats.get(b.stat);
    const label = `${num(b.threshold)} ${st?.scoring?.plural ?? st?.name.toLowerCase() ?? b.stat}`;
    const bad = outside(b.points, SCORING_LIMITS.bonusPoints);
    if (bad) err(`scoring.bonuses.${i}`, `A bonus for ${label}: ${bad}.`);
    const { min, max } = SCORING_LIMITS.threshold;
    if (b.threshold < min || b.threshold > max)
      err(
        `scoring.bonuses.${i}`,
        `A bonus for ${label}: the threshold runs from ${min} to ${max}.`,
      );
  });
  scoring.tiers.forEach((t, i) => {
    if (same(t, before?.tiers)) return;
    const st = stats.get(t.stat);
    t.bands.forEach((band, j) => {
      const bad = outside(band.points, SCORING_LIMITS.bandPoints);
      if (bad) err(`scoring.tiers.${i}.bands.${j}`, `${bandLabel(st, band)}: ${bad}.`);
    });
  });
  return issues;
}
